import React, { Component } from 'react';
import { IbssPage } from '../../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../../AppContext';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Box, Grid, Typography } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import IbssActionButton, { IActionButton } from '../../../../../Components/Buttons/ActionButton/IbssActionButton';
import { BaseQueryParams } from '../../../../../Components/Core/BasePage/BaseQueryParams';
import IbssSvgIcon from '../../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Bin from '../../../../../Components/Icons/Bin';
import ConfirmModal from '../../../../../Components/Dialogs/ConfirmDialog/ConfirmModal';
import IbssFilter from '../../../../../Components/Forms/Filter/IbssFilter';
import IbssSnackbar from '../../../../../Components/Snackbar/IbssSnackbar';
import Helper, { getFloorUsingFloorId } from '../../../../../Common/Helper';
import { PagedResponse } from '../../../../../Providers.Api/Models';
import { storeDetails } from './DataModels';
import IbssButton from '../../../../../Components/Buttons/Button/IbssButton';
import ServerDataGrid, { DataGridQueryResult, IDataQuery, IPages } from '../../../../../Components/Data/ServerDataGrid/ServerDataGrid';
import EditStoreDrawer from './EditStore';
import IbssPageHeader from '../../../../../Components/Headings/PageHeading/IbssPageHeader';

class ListStores extends IbssPage<IProps, IState, QueryParams>
{
    private dataGridRef = React.createRef<ServerDataGrid<IEquipmentStore>>();
    private get dataGrid() { return this.dataGridRef.current; }
    private get labels() { return appContext().labels; }
    private get appState() { return appContext().state; }
    private columns: GridColDef<IEquipmentStore>[];

    constructor(props: IProps)
    {
        super(props, new QueryParams());

        this.state =
        {
            buildingId: this.appState.buildingId,
            isLoading: false,
            openDrawer: false,
            showConfirmDialog: false,
            storeSavedSnackBar: false,
            filterText: '',
            selectedStores: [],
            pagesOfEquipmentStore: {
                pages: [],
                pageIndex: 0,
                skipToken: '',
            },
        };

        this.columns = this.initializeDataGridColumns
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcEquipment_S
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        const queryParams = this.queryParams
        await this.setStateAsync({ buildingId: queryParams.building ?? 1 });
        await this.dataGrid?.refresh();
    }

    private async dataQueryChanged(gridQuery: IDataQuery): Promise<DataGridQueryResult<IEquipmentStore>>    
    {

        const storeData = await appContext().ibssApiClientV2.v2.byNodeid.spaces.get<PagedResponse<storeDetails[]>>({
            nodeId: this.state.buildingId,
            select: storeDetails,
            filter: `Space_Type eq 'EquipmentRoom'`,
        });

        const store: IEquipmentStore[] = storeData.value.map(i => ({
            id: i.Space_Id,
            floorId: i.Node_Id,
            floorName: getFloorUsingFloorId(i.Node_Id),
            status: i.Space_IsEnabled ? 'Active' : 'Inactive',
            storeName: i.Space_Name,
            storeCustomInfo: i.Space_Custom_Info,
        }));
        this.setState({ isLoading: false });
        return new DataGridQueryResult(store, storeData.skipToken);
    }

    private filterStores(searchTerm: string): IPages<IEquipmentStore>
    {
        if (!searchTerm)
        {
            return this.state.pagesOfEquipmentStore;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        const store = this.state.pagesOfEquipmentStore.pages.flatMap(x => x.rows)
            .filter(equipment =>
                equipment.storeName.toLowerCase().includes(lowerSearchTerm) ||
                equipment.floorName.toLowerCase().includes(lowerSearchTerm) ||
                equipment.status.toLowerCase().includes(lowerSearchTerm)
            );

        return { pages: [{ rows: store }], skipToken: '', pageIndex: 0 };
    }

    private async enableStore(): Promise<void> 
    {
        this.setState({ isLoading: true });
        try 
        {
            this.setState({ isLoading: true });
            const body = this.state.selectedStores.map(store => ({
                Space_Id: store.id,
                Node_Id: store.floorId,
            }));

            await appContext().ibssApiClientV2.v2.byNodeid.spaces.enable.patch({
                nodeId: this.state.buildingId,
                body: body
            })

            await this.dataGrid?.refresh();
            this.setState({ storeSavedSnackBar: true, selectedStores: [] });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async disableStore(): Promise<void> 
    {
        this.setState({ isLoading: true });
        try 
        {
            this.setState({ isLoading: true });

            const body = this.state.selectedStores.map(store => ({
                Space_Id: store.id,
                Node_Id: store.floorId,
            }));

            await appContext().ibssApiClientV2.v2.byNodeid.spaces.disable.patch({
                nodeId: this.state.buildingId,
                body: body
            })

            await this.dataGrid?.refresh();
            this.setState({ storeSavedSnackBar: true, selectedStores: [] });
        }
        finally
        {
            this.setState({ isLoading: false, showConfirmDialog: false });
        }

    }

    private async deleteStore(): Promise<void> 
    {
        this.setState({ isLoading: true });
        try 
        {
            this.setState({ isLoading: true });

            await Promise.all(
                this.state.selectedStores.map(store =>
                    appContext().ibssApiClientV2.v2.byNodeid.spaces.byId.delete({
                        nodeId: store.floorId,
                        id: store.id,
                    })
                )
            );

            await this.dataGrid?.refresh();
        }
        finally
        {
            this.setState({ isLoading: false, showConfirmDialog: false });
        }
    }

    private handleSelectionChange(selection: GridRowSelectionModel): void
    {
        const pageOfRows = this.dataGrid?.rows as IEquipmentStore[];
        const selectedRows = pageOfRows.filter(row => selection.includes(row.id))
        this.setState({ selectedStores: selectedRows });
    }

    private handleButtonDisable(status: "Enable" | "Disable" | "Delete"): boolean
    {
        const { selectedStores } = this.state;

        if (!selectedStores.length)
        {
            return true;
        }

        const hasActive = selectedStores.some(({ status }) => status === "Active");
        const hasInactive = selectedStores.some(({ status }) => status === "Inactive");

        switch (status)
        {
            case "Enable":
                return hasActive || !hasInactive;
            case "Disable":
                return hasInactive || !hasActive;
            case "Delete":
                return false;
            default:
                return true;
        }
    }

    private showConfirmModal(): void
    {
        this.setState({
            showConfirmDialog: true
        });
    }

    private updateAssignedSpaces(store: IEquipmentStore): React.ReactNode
    {
        return (
            <IbssButton variant='text' sx={{ textDecoration: "underline" }} onClick={() => this.props.history.push(`/admin/store/assign/${store.id}`)}>
                {this.labels.HubLabelUpdate}
            </IbssButton>
        )
    }

    private handleUpdate(store: IEquipmentStore): React.ReactNode
    {
        return (
            <IbssButton variant='text' sx={{ textDecoration: "underline" }} onClick={() => this.setState({ openDrawer: true, selectedStores: [store] })}>
                {this.labels.HubLabelView}
            </IbssButton>
        )
    }

    private closeDrawer(): void
    {
        this.setState({
            openDrawer: false,
            selectedStores: []
        });
    }

    private renderStatus(status: string): React.ReactNode {
        return status === 'Active' ? this.labels.HubLabelActive : this.labels.HubLabelInActive;
    }

    public get initializeDataGridColumns(): GridColDef<IEquipmentStore>[]
    {
        const dataGridColumns: GridColDef<IEquipmentStore>[] =
            [
                {
                    field: Helper.nameOf<IEquipmentStore>('storeName'),
                    headerName: this.labels.funcEquipmentStore_S,
                    width: 250,
                },
                {
                    field: Helper.nameOf<IEquipmentStore>('id'),
                    headerName: this.labels.HubLabelId,
                    width: 250,
                },
                {
                    field: Helper.nameOf<IEquipmentStore>('floorName'),
                    headerName: this.labels.HubLabelFloor,
                    width: 250,
                },
                {
                    field: Helper.nameOf<IEquipmentStore>('status'),
                    headerName: this.labels.HubLabelStatus,
                    renderCell: (params) => this.renderStatus(params.row.status),
                    width: 250,
                },
                {
                    field: 'action',
                    headerName: this.labels.HubLabelAction,
                    width: 250,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.handleUpdate(params.row)}</>
                },
                {
                    field: '',
                    headerName: this.labels.funcAssignedSpaces_S,
                    width: 250,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.updateAssignedSpaces(params.row)}</>
                }
            ];

        return dataGridColumns;
    }

    public get actionButtons(): IActionButton[] 
    {
        const actionButtons: IActionButton[] =
            [
                {
                    label: this.labels.HubButtonDelete,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <Bin />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.showConfirmModal(),
                    disabled: this.handleButtonDisable("Delete"),
                },
                {
                    label: this.labels.HubLabelInActive,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <CancelIcon />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.disableStore(),
                    disabled: this.handleButtonDisable("Disable"),
                },
                {
                    label: this.labels.HubLabelActive,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <CheckCircleIcon />
                        </IbssSvgIcon>
                    ),
                    color: "info",
                    onClick: () => this.enableStore(),
                    disabled: this.handleButtonDisable("Enable"),
                },
            ];
        return actionButtons
    }

    public render(): JSX.Element
    {
        const filteredStore = this.filterStores(this.state.filterText);
        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <div className="table-panel">
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                <IbssPageHeader
                                        title={this.labels.funcEquipmentStores_L}
                                        description={this.labels.funcEditEquipmentStore_D}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <IbssFilter
                                        searchTerm={this.state.filterText}
                                        onSearchTermChange={(event) => this.setState({ filterText: event.target.value })}
                                    />
                                    <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', my: 1, mr: 0 }}>
                                        <IbssActionButton buttons={this.actionButtons} />
                                    </Box>
                                    {
                                        <div>
                                            <ServerDataGrid
                                                ref={this.dataGridRef}
                                                loadDataOnMount={false}
                                                checkboxSelection
                                                columns={this.columns}
                                                disableRowSelectionOnClick
                                                pageSizeOptions={[100]}
                                                pages={filteredStore}
                                                onDataQueryChange={i => this.dataQueryChanged(i)}
                                                onPagesChange={pages => this.setStateAsync({ pagesOfEquipmentStore: pages })}
                                                onRowSelectionModelChange={e => this.handleSelectionChange(e)}
                                            />
                                        </div>
                                    }
                                </Grid>
                            </Grid>
                        </Box>
                        <ConfirmModal
                            show={this.state.showConfirmDialog}
                            modalHeading={this.labels.HubLabelModalConfirmTitle}
                            modalMessage={this.labels.funcAnnouncementsConfirmDelete_Message}
                            handleModal={() => this.setState({ showConfirmDialog: false })}
                            okButton={() => this.deleteStore()}
                        />
                        {this.state.openDrawer &&
                            <EditStoreDrawer
                                buildingId={this.state.buildingId}
                                selectedStore={this.state.selectedStores[0]}
                                open={this.state.openDrawer}
                                onClose={() => this.closeDrawer()}
                            />
                        }
                    </div>
                </div>
                <IbssSnackbar
                    open={this.state.storeSavedSnackBar}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ storeSavedSnackBar: false })}
                    message={this.labels.funcStoreSnackbar_Message}
                    action={<CheckCircleIcon color='success' />}
                />
            </div>
        )
    }

}

export default withRouter(ListStores);

export class QueryParams extends BaseQueryParams
{
}

export interface IProps extends RouteComponentProps 
{
}

export interface IState 
{
    buildingId: number;
    isLoading: boolean;
    openDrawer: boolean;
    filterText: string;
    selectedStores: IEquipmentStore[];
    showConfirmDialog: boolean;
    storeSavedSnackBar: boolean;
    pagesOfEquipmentStore: IPages<IEquipmentStore>;
}

export interface IEquipmentStore
{
    id: string;
    storeName: string;
    floorName: string;
    floorId: number;
    status: 'Active' | 'Inactive';
    storeCustomInfo: string;
}